import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, useToggle } from '@factorialco/deprecated-design-system'
import { lastDayOfMonth, startOfMonth, subMonths } from 'date-fns'
import {
  InsightsFiltersOptionsQuery,
  InsightsInsightsPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ExportInsightsModal from 'modules/insights/components/ExportInsightsModal'
import Filters from 'modules/insights/components/Filters'
import WidgetAgeRangeDistribution from 'modules/insights/components/WidgetAgeRangeDistribution'
import WidgetApprovedPromotionsByTeam from 'modules/insights/components/WidgetApprovedPromotionsByTeam'
import WidgetEmployeeAverageAge from 'modules/insights/components/WidgetEmployeeAverageAge'
import WidgetEmployeesPerLegalEntity from 'modules/insights/components/WidgetEmployeesPerLegalEntity'
import WidgetHeadcountByGender from 'modules/insights/components/WidgetHeadcountByGender'
import WidgetHeadcountByTeam from 'modules/insights/components/WidgetHeadcountByTeam'
import WidgetHeadcountEvolution from 'modules/insights/components/WidgetHeadcountEvolution'
import WidgetRetention from 'modules/insights/components/WidgetRetention'
import WidgetTurnoverRate from 'modules/insights/components/WidgetTurnoverRate'
import {
  CycleDateRangeWithSetters,
  useCycleDateRange,
} from 'modules/insights/hooks/useCycleDateRange'
import { InsightsFilters } from 'modules/insights/hooks/useInsightsFilters'
import WidgetMetrics from 'modules/insights/lib/widgetMetrics'
import { SELECTED_RANGE_PARAM } from 'modules/insights/pages/HeadcountPage'
import { useInsightsContext } from 'modules/insights/pages/RootPage'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type PageContentProps = {
  widgetMetricsObject: WidgetMetrics
  filterOptionsData: InsightsFiltersOptionsQuery
  filters: InsightsFilters
  cycleDateRange: CycleDateRangeWithSetters
  widgetContainerRef: React.RefObject<HTMLElement>
  isLoading: boolean
  setFilter: <T extends keyof InsightsFilters>(filterType: T, filter: InsightsFilters[T]) => void
  clearFilters: () => void
}

const PageContent = ({
  widgetMetricsObject,
  filterOptionsData,
  filters,
  cycleDateRange,
  widgetContainerRef,
  isLoading,
  setFilter,
  clearFilters,
}: PageContentProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const widgetMetrics = widgetMetricsObject.generate()

  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()
  useEffect(() => {
    track(trackingEvents.insights.openInsightsPage, {
      company_id: companyId,
      access_id: accessId,
    })
  }, [])

  const [isExportModalOpen, toggleExportModal] = useToggle(false)

  return (
    <Box flexDirection="column" gap="s32">
      <Box flexGrow flexDirection="row">
        <Box alignItems="flexStart" style={{ width: '95%' }}>
          <Filters
            filters={filters}
            setFilter={setFilter}
            isLoading={isLoading}
            clearFilters={clearFilters}
            filterOptionsData={filterOptionsData}
          />
        </Box>
        <Box alignItems="flexEnd">
          <Button iconName="download" onClick={toggleExportModal(true)}>
            {i18n.t('common.export')}
          </Button>
        </Box>
      </Box>

      <Grid variant="fixed" ref={widgetContainerRef}>
        <Box justifyContent="flexStart" gridColSpan={{ xxs: 12 }}>
          <WidgetHeadcountEvolution
            headcountEvolution={widgetMetrics.headcountEvolution}
            cycleDateRange={cycleDateRange}
            onXAxisSelected={(xAxis, action) => {
              track(trackingEvents.insights.headcountPage.open, { action })

              navigate({
                pathname: navigation.insights.headcount.fullPath(),
                search: `?${SELECTED_RANGE_PARAM}=${dateToString(xAxis, 'backend')}`,
              })
            }}
          />
        </Box>
        <Box gridColSpan={{ xxs: 12, m: 6 }}>
          <WidgetRetention retention={widgetMetrics.retention} />
        </Box>
        <Box gridColSpan={{ xxs: 12, m: 6 }}>
          <WidgetTurnoverRate turnover={widgetMetrics.turnover} />
        </Box>
        <Box justifyContent="flexStart" gridColSpan={{ xxs: 12, m: 6 }}>
          <WidgetEmployeesPerLegalEntity
            employeesPerLegalEntity={widgetMetrics.employeesPerLegalEntity}
          />
        </Box>
        <Box gridColSpan={{ xxs: 12, m: 6 }}>
          <WidgetHeadcountByGender headcountByGender={widgetMetrics.headcountByGender} />
        </Box>
        <Box gridColSpan={{ xxs: 12 }}>
          <WidgetHeadcountByTeam headcountByTeam={widgetMetrics.headcountByTeam} />
        </Box>
        <Box gridColSpan={{ xxs: 12, m: 3 }}>
          <WidgetEmployeeAverageAge employeesAverageAge={widgetMetrics.employeesAverageAge} />
        </Box>
        <Box gridColSpan={{ xxs: 12, m: 9 }}>
          <WidgetAgeRangeDistribution ageRangeDistribution={widgetMetrics.ageRangeDistribution} />
        </Box>
        <Box gridColSpan={{ xxs: 12, m: 6 }}>
          <WidgetApprovedPromotionsByTeam promotionsByTeam={widgetMetrics.promotionsByTeam} />
        </Box>
      </Grid>

      <ExportInsightsModal
        insightsDOM={widgetContainerRef}
        widgetMetric={widgetMetricsObject}
        open={isExportModalOpen}
        onClose={toggleExportModal(false)}
      />
    </Box>
  )
}

const InsightsPage = () => {
  const { filters, setFilter, clearFilters, filterOptionsData, filterOptionsError } =
    useInsightsContext()

  const cycleDateRange = useCycleDateRange(() => {
    const currentDate = new Date()

    return {
      cycle: 'monthly',
      startDate: startOfMonth(subMonths(currentDate, 11)),
      endDate: lastDayOfMonth(currentDate),
    }
  })

  // For Turnover and retention calculations
  const ratesEndDate = useMemo(() => lastDayOfMonth(new Date()), [])
  const ratesStartDate = useUnsafeMemo(
    () => startOfMonth(subMonths(ratesEndDate, 15)),
    [ratesEndDate]
  )

  const { company } = useCurrentSession()

  const {
    data,
    loading: loadingFilteredData,
    error,
  } = useQuery(InsightsInsightsPageDocument, {
    variables: {
      companyId: company.id,
      cycle: cycleDateRange.cycle,
      startDate: dateToString(cycleDateRange.startDate, 'backend'),
      endDate: dateToString(cycleDateRange.endDate, 'backend'),

      ratesStartDate: dateToString(ratesStartDate, 'backend'),
      ratesEndDate: dateToString(ratesEndDate, 'backend'),

      // Filters
      genders: filters.genders?.filter((gender): gender is string => gender != null),
      withNoGender: filters.genders?.includes(null) ?? false,
      legalEntities: filters.legalEntities,
      workplaces: filters.workplaces,
      managers: filters.managers?.filter((manager): manager is number => manager != null),
      withNoManager: filters.managers?.includes(null) ?? false,
      teams: filters.teams,
      nationalities: filters.nationalities?.filter(
        (nationality): nationality is string => nationality != null
      ),
      withNoNationality: filters.nationalities?.includes(null) ?? false,
    },
  })

  // Cache last data to avoid showing a loading page when filters change
  const [lastData, setLastData] = React.useState(data ? data : undefined)

  useUnsafeEffect(() => {
    if (data) {
      setLastData(data)
    }
  }, [data])

  const currentData = data ?? lastData

  const widgetContainerRef = React.useRef<HTMLElement>(null)

  const widgetMetric = currentData && new WidgetMetrics(currentData, cycleDateRange)

  return (
    <ResponsivePage.Body loading={!currentData} error={error ?? filterOptionsError} variant="full">
      {widgetMetric && filterOptionsData && (
        <>
          <PageContent
            widgetMetricsObject={widgetMetric}
            filterOptionsData={filterOptionsData}
            filters={filters}
            cycleDateRange={cycleDateRange}
            widgetContainerRef={widgetContainerRef}
            isLoading={loadingFilteredData}
            setFilter={setFilter}
            clearFilters={clearFilters}
          />
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default InsightsPage
